import { z } from 'zod';
const text = z.string().max(4000);
const decimal = z
  .string()
  .max(30)
  .regex(/^(0|[1-9]\d*)(\.\d+)?$/);
export const developmentPhotoSchema = z
  .object({
    fileId: z.uuid(),
    derivativeId: z.uuid(),
    side: z.enum(['front', 'back', 'label']),
    rotation: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]),
    rawOcr: text,
    correctedOcr: text,
    quality: z.enum(['unreviewed', 'readable', 'blurred', 'incomplete']),
  })
  .strict();
export const developmentEvidenceSchema = z
  .object({
    audience: z.enum(['customer', 'internal']),
    field: z.string().min(1).max(100),
    value: text,
    source: z.string().min(1).max(500),
    url: z.union([z.literal(''), z.url().refine((v) => /^https?:\/\//.test(v))]),
    observedAt: z.iso.date(),
    uncertainty: z.enum(['unverified', 'supported', 'conflicting', 'inaccessible']),
  })
  .strict();
export const developmentIntakeSchema = z
  .object({
    label: z.string().min(1).max(200),
    referenceUrl: z
      .union([
        z.literal(''),
        z
          .url()
          .max(2000)
          .refine((v) => /^https?:\/\//.test(v)),
      ])
      .optional(),
    category: z.enum(['unknown', 'moisturizer', 'body_wash', 'cosmetic_serum', 'specialist']),
    market: text,
    texture: text,
    fragrance: text,
    benefits: text,
    avoidedIngredients: text,
    fillSize: decimal.nullable(),
    fillUnit: z.enum(['g', 'mL']),
    quantity: decimal.nullable(),
    factoryTargetPrice: decimal.nullable(),
    retailTargetPrice: decimal.nullable(),
    currency: z.enum(['THB']),
    densityGml: decimal.nullable(),
    yieldFraction: decimal.nullable(),
  })
  .strict();
export const developmentLineSchema = z
  .object({
    inci: z.string().min(1).max(500),
    function: text,
    grade: text,
    phase: z.enum(['A', 'B', 'C']),
    percent: decimal,
    activeFraction: decimal,
    supplierPricePerKg: decimal.nullable(),
    priceSource: text,
    priceClass: z.enum(['unknown', 'assumption', 'historical', 'quote']),
    priceDate: z.union([z.literal(''), z.iso.date()]),
  })
  .strict();
export const developmentDirectionSchema = z
  .object({
    label: z.string().min(1).max(200),
    origin: z.enum(['approved_base', 'modified_base', 'new_experimental']),
    libraryId: z.uuid().nullable(),
    libraryVersion: z.int().positive().optional(),
    libraryHash: z
      .string()
      .regex(/^[a-f0-9]{64}$/)
      .optional(),
    rationale: text,
    lines: z.array(developmentLineSchema).max(60),
    process: text,
    equipment: text,
    targets: text,
    preservation: text,
    tests: text,
    compatibility: text,
    substitutions: text,
    revalidation: text,
    assumptions: text,
    risks: text,
    balancing: text,
  })
  .strict();
export const developmentContentSchema = z
  .object({
    intake: developmentIntakeSchema,
    photos: z.array(developmentPhotoSchema).max(12),
    candidates: z
      .array(
        z
          .object({
            brand: text,
            product: text,
            referenceMarket: text,
            referenceSize: text,
            identity: text,
            variant: text,
            inciLists: z.array(text).max(10),
            evidence: z.array(developmentEvidenceSchema).max(50),
          })
          .strict(),
      )
      .max(10),
    selectedCandidate: z.int().min(0).max(9).nullable(),
    identityConfirmed: z.boolean(),
    briefConfirmed: z.boolean(),
    directions: z.array(developmentDirectionSchema).max(3),
    selectedDirection: z.int().min(0).max(2).nullable(),
    sample: z
      .object({
        requestId: z.uuid().nullable(),
        sampleId: z.uuid().nullable(),
        sampleHash: z
          .string()
          .regex(/^[a-f0-9]{64}$/)
          .nullable(),
        feedback: text,
        changeRequest: text,
      })
      .strict(),
    costs: z
      .array(
        z
          .object({
            label: z.string().min(1).max(100),
            amountTHB: decimal.nullable(),
            basis: z.enum(['per_unit', 'per_batch']),
            source: text,
            date: z.union([z.literal(''), z.iso.date()]),
            provenance: z.enum(['unknown', 'assumption', 'historical', 'quote']),
          })
          .strict(),
      )
      .max(20),
    revisionReason: text,
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.selectedCandidate !== null && !v.candidates[v.selectedCandidate])
      ctx.addIssue({
        code: 'custom',
        path: ['selectedCandidate'],
        message: 'Candidate unavailable',
      });
    if (v.selectedDirection !== null && !v.directions[v.selectedDirection])
      ctx.addIssue({
        code: 'custom',
        path: ['selectedDirection'],
        message: 'Direction unavailable',
      });
  });
export type DevelopmentContent = z.infer<typeof developmentContentSchema>;
export type DevelopmentDirection = z.infer<typeof developmentDirectionSchema>;
export function emptyDevelopment(label = 'ร่างอ้างอิงใหม่'): DevelopmentContent {
  return {
    intake: {
      label,
      referenceUrl: '',
      category: 'unknown',
      market: '',
      texture: '',
      fragrance: '',
      benefits: '',
      avoidedIngredients: '',
      fillSize: null,
      fillUnit: 'g',
      quantity: null,
      factoryTargetPrice: null,
      retailTargetPrice: null,
      currency: 'THB',
      densityGml: null,
      yieldFraction: null,
    },
    photos: [],
    candidates: [],
    selectedCandidate: null,
    identityConfirmed: false,
    briefConfirmed: false,
    directions: [],
    selectedDirection: null,
    sample: { requestId: null, sampleId: null, sampleHash: null, feedback: '', changeRequest: '' },
    costs: ['packaging', 'labour', 'tests', 'freight', 'other'].map((label) => ({
      label,
      amountTHB: null,
      basis: 'per_batch' as const,
      source: '',
      date: '',
      provenance: 'unknown' as const,
    })),
    revisionReason: '',
  };
}
export const developmentMutationSchema = z
  .object({
    projectId: z.uuid(),
    anchorId: z.uuid(),
    baseId: z.uuid().nullable(),
    baseHash: z
      .string()
      .regex(/^[a-f0-9]{64}$/)
      .nullable(),
    idempotencyKey: z.uuid(),
    content: developmentContentSchema,
  })
  .strict();
